import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, ScanLine, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApiService } from '../../hooks/use-api-service';
import { cn } from '../../lib/utils';
import { Receipt } from '../../types/expense';
import { ReviewReceiptDialog } from './review-receipt-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { receiptTypeService } from '../../services/receiptTypeService';
import { useDataMode } from '../../providers/DataModeProvider';

interface UploadReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ScanStage = 'idle' | 'uploading' | 'scanning' | 'extracting' | 'analyzing' | 'complete' | 'error';

interface ScanProgress {
  stage: ScanStage;
  message: string;
  progress: number;
}

export function UploadReceiptDialog({ open, onOpenChange }: UploadReceiptDialogProps) {
  const { api } = useApiService();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLiveMode } = useDataMode();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isScreenScanning, setIsScreenScanning] = useState(false);
  const [screenScanProgress, setScreenScanProgress] = useState(0);
  const [scanProgress, setScanProgress] = useState<ScanProgress>({
    stage: 'idle',
    message: 'Ready to upload',
    progress: 0,
  });
  const [scannedReceipt, setScannedReceipt] = useState<Receipt | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioningToReview, setIsTransitioningToReview] = useState(false);
  const [selectedReceiptType, setSelectedReceiptType] = useState<string>('');

  // Fetch receipt types
  const { data: receiptTypes = [], isLoading: isLoadingReceiptTypes } = useQuery({
    queryKey: ['receiptTypes', isLiveMode],
    queryFn: () => receiptTypeService.getAll(isLiveMode),
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsDragging(false);
      setIsScreenScanning(false);
      setScreenScanProgress(0);
      setScanProgress({ stage: 'idle', message: 'Ready to upload', progress: 0 });
      setScannedReceipt(null);
      // Set default receipt type - find the one marked as default, or use first available
      if (receiptTypes.length > 0 && !selectedReceiptType) {
        const defaultType = receiptTypes.find(t => t.default);
        setSelectedReceiptType(defaultType?.id || receiptTypes[0].id);
      }
    }
  }, [open, receiptTypes, selectedReceiptType]);

  // Ref for paste-enabled textarea
  const pasteAreaRef = useRef<HTMLDivElement>(null);

  // Focus the paste area when dialog opens
  useEffect(() => {
    if (open && !selectedFile) {
      setTimeout(() => {
        pasteAreaRef.current?.focus();
      }, 150);
    }
  }, [open, selectedFile]);

  // Handle paste from clipboard
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    // Only handle paste when dialog is open and not already processing
    const isCurrentlyProcessing = ['uploading', 'scanning', 'extracting'].includes(scanProgress.stage);
    if (!open || isCurrentlyProcessing || selectedFile) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    // Look for image in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          // Create a File object with a proper name
          const file = new File([blob], `pasted-receipt-${Date.now()}.png`, { type: blob.type });
          processFile(file);
          toast.success('Image pasted from clipboard');
        }
        break;
      }
    }
  };

  // Note: Paste handling is done via onPaste on the contentEditable div

  // Smooth continuous scan line animation
  useEffect(() => {
    if (!isScreenScanning) return;

    const duration = 3000; // 3 seconds for full scan
    const fps = 60; // 60 frames per second
    const totalFrames = (duration / 1000) * fps;
    const increment = 100 / totalFrames;
    
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 0; // Loop the animation
      }
      setScreenScanProgress(currentProgress);
    }, 1000 / fps);

    return () => clearInterval(interval);
  }, [isScreenScanning]);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      let uploadedReceipt: Receipt | null = null;
      
      try {
        // Step 1: Upload the receipt with receipt type
        uploadedReceipt = await api.uploadReceipt(file, selectedReceiptType);
        
        if (!uploadedReceipt || !uploadedReceipt.id) {
          throw new Error('Invalid receipt data received from server');
        }
        
        // Step 2: Call OCR API with the receipt ID
        setScanProgress({ stage: 'scanning', message: 'Scanning document...', progress: 50 });
        const ocrResult = await api.scanReceiptOcr(uploadedReceipt.id);
        console.log('=== OCR RESULT DEBUG ===');
        console.log('OCR Result:', ocrResult);
        console.log('OCR Result keys:', Object.keys(ocrResult));
        console.log('potentialDuplicates:', ocrResult.potentialDuplicates);
        console.log('=== END OCR DEBUG ===');
        
        // Step 3: Merge OCR results with the receipt
        // Note: Expense type detection is handled in the review dialog
        const receiptWithOcr: Receipt & { potentialDuplicates?: any[] } = {
          ...uploadedReceipt,
          transactionDate: ocrResult.transactionDate,
          amount: ocrResult.totalAmount,
          supplier: ocrResult.merchantName,
          annotatedImageData: ocrResult.annotatedImageBase64,
          potentialDuplicates: ocrResult.potentialDuplicates,
        };
        
        return receiptWithOcr;
      } catch (error) {
        console.error('Upload mutation error:', error);
        
        // If OCR failed but receipt was uploaded, clean it up
        if (uploadedReceipt && uploadedReceipt.id) {
          try {
            await api.deleteReceipt(uploadedReceipt.id);
            console.log('Cleaned up receipt after OCR failure');
          } catch (deleteError) {
            console.error('Failed to clean up receipt after OCR failure:', deleteError);
          }
        }
        
        throw error;
      }
    },
    onMutate: () => {
      setScanProgress({ stage: 'uploading', message: 'Uploading receipt...', progress: 25 });
    },
    onSuccess: async (data) => {
      try {
        setScanProgress({ stage: 'extracting', message: 'Extracting data...', progress: 85 });
        await new Promise(resolve => setTimeout(resolve, 150));
        
        setScanProgress({ stage: 'complete', message: 'Scan complete!', progress: 100 });
        
        // Quick transition to review dialog
        await new Promise(resolve => setTimeout(resolve, 200));
        setIsScreenScanning(false);
        setScannedReceipt(data);
        setIsTransitioningToReview(true); // Mark that we're transitioning to review
        setReviewDialogOpen(true);
        onOpenChange(false); // Close upload dialog
      } catch (error) {
        console.error('Success handler error:', error);
        setIsScreenScanning(false);
        toast.error('An error occurred while processing the receipt');
      }
    },
    onError: (error) => {
      console.error('Upload error:', error);
      setIsScreenScanning(false);
      setScanProgress({ 
        stage: 'error', 
        message: error instanceof Error ? error.message : 'Failed to upload receipt', 
        progress: 0 
      });
      toast.error('Failed to upload receipt');
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    // Start screen scanning effect and upload simultaneously
    setIsScreenScanning(true);
    setScreenScanProgress(0);
    uploadMutation.mutate(selectedFile);
  };

  const handleClose = async () => {
    // If there's a scanned receipt that hasn't been saved and we're NOT transitioning to review, delete it
    // This handles the case where user clicks X after upload but before reviewing
    if (scannedReceipt && !isSaving && !isTransitioningToReview) {
      try {
        await api.deleteReceipt(scannedReceipt.id);
        toast.success('Receipt discarded');
      } catch (error) {
        console.error('Failed to delete receipt:', error);
        toast.error('Failed to discard receipt');
      }
    }
    
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsDragging(false);
    setScanProgress({ stage: 'idle', message: 'Ready to upload', progress: 0 });
    setScannedReceipt(null);
    setIsTransitioningToReview(false); // Reset the flag
    onOpenChange(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const processFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    setScanProgress({ stage: 'idle', message: 'Ready to scan', progress: 0 });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleReviewConfirm = async (updatedReceipt: Partial<Receipt>) => {
    if (!scannedReceipt) return;

    try {
      setIsSaving(true);
      
      // Merge the updated fields with the original scanned receipt (including imageData)
      const completeReceipt = {
        ...scannedReceipt,
        ...updatedReceipt,
      };
      
      await api.updateReceipt(scannedReceipt.id, completeReceipt);
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      await queryClient.invalidateQueries({ queryKey: ['processedReceipts'] });
      
      // Add delay to ensure spinner stays visible while data refreshes
      await new Promise(resolve => setTimeout(resolve, 800));
      
      toast.success('Receipt saved successfully!');
      setReviewDialogOpen(false);
      setScannedReceipt(null);
      handleClose();
    } catch (error) {
      console.error('Failed to update receipt:', error);
      toast.error('Failed to save receipt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviewDiscard = async () => {
    if (!scannedReceipt) return;

    try {
      await api.deleteReceipt(scannedReceipt.id);
      toast.success('Receipt discarded');
      setScannedReceipt(null); // Clear the receipt before closing to prevent double deletion
      setReviewDialogOpen(false);
      handleClose();
    } catch (error) {
      console.error('Failed to delete receipt:', error);
      toast.error('Failed to discard receipt');
    }
  };

  const handleReviewDialogClose = async (open: boolean) => {
    // If closing the dialog (open = false) and there's a scanned receipt that hasn't been saved
    // Only delete if scannedReceipt still exists (not already deleted by handleReviewDiscard)
    if (!open && scannedReceipt && !isSaving) {
      try {
        // Delete the receipt from the server
        await api.deleteReceipt(scannedReceipt.id);
        toast.success('Receipt discarded');
        setScannedReceipt(null);
      } catch (error) {
        console.error('Failed to delete receipt:', error);
        toast.error('Failed to discard receipt');
      }
    }
    setReviewDialogOpen(open);
  };

  const isProcessing = ['uploading', 'scanning', 'extracting'].includes(scanProgress.stage);
  const isComplete = scanProgress.stage === 'complete';
  const isError = scanProgress.stage === 'error';

  return (
    <>
      {/* Loading Overlay for Saving - Outside dialogs so it shows even when upload dialog is closed */}
      {isSaving && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="bg-card p-6 rounded-lg shadow-lg flex flex-col items-center gap-4 border">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">Saving receipt...</p>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent 
          className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Upload Receipt</DialogTitle>
            <DialogDescription>
              Upload a receipt image to extract expense information using OCR. Press Ctrl+V to paste an image.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Receipt Type Selector */}
            <div className="space-y-2">
              <Label htmlFor="receipt-type">Receipt Type *</Label>
              <Select
                value={selectedReceiptType}
                onValueChange={setSelectedReceiptType}
                disabled={isLoadingReceiptTypes || isProcessing}
              >
                <SelectTrigger id="receipt-type">
                  <SelectValue placeholder="Select receipt type" />
                </SelectTrigger>
                <SelectContent>
                  {receiptTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${type.color}`} />
                        <span>{type.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Upload Area */}
            {!selectedFile && (
              <div className="space-y-3">
                <div
                  className="relative"
                >
                  <div
                    ref={pasteAreaRef}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onPaste={handlePaste}
                    onContextMenu={(e) => {
                      // Ensure the element is focused when right-clicking
                      const target = e.currentTarget as HTMLDivElement;
                      target.focus();
                      console.log('Right-click detected, element focused');
                      // Let the browser show its default context menu
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleBrowseClick}
                    onKeyDown={(e) => {
                      // Prevent typing, only allow paste shortcuts
                      if (!e.ctrlKey && !e.metaKey && e.key !== 'v' && e.key !== 'V') {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      // Clear any text that gets typed
                      const target = e.target as HTMLDivElement;
                      if (target.textContent) {
                        target.textContent = '';
                      }
                    }}
                    className={cn(
                      "border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 relative cursor-pointer outline-none",
                      isDragging 
                        ? "border-primary bg-primary/10 scale-[1.02]" 
                        : "border-muted-foreground/25"
                    )}
                    style={{ caretColor: 'transparent' }}
                  >
                    <Upload className={cn(
                      "h-12 w-12 mx-auto mb-4 transition-colors pointer-events-none",
                      isDragging ? "text-primary" : "text-muted-foreground"
                    )} />
                    <p className="text-sm font-medium mb-1 pointer-events-none">
                      {isDragging ? "Drop receipt here" : "Drag & drop, paste, or click to upload"}
                    </p>
                    <p className="text-xs text-muted-foreground pointer-events-none">
                      PNG, JPG, or JPEG (max 10MB)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Preview and Scan Area */}
            {selectedFile && (
              <div className="space-y-4">
                {/* Image Preview */}
                <div className="relative rounded-lg overflow-hidden border bg-muted/20">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Receipt preview"
                      className="w-full h-48 sm:h-64 object-contain"
                    />
                  )}
                  
                  {/* Screen Scanning Animation */}
                  {isScreenScanning && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Animated scan line with glow */}
                      <div 
                        className="absolute left-0 right-0 h-2 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_40px_rgba(6,182,212,0.9)]"
                        style={{
                          top: `${screenScanProgress}%`,
                          filter: 'blur(1px)'
                        }}
                      />
                      
                      {/* Secondary scan line for depth */}
                      <div 
                        className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-60"
                        style={{
                          top: `${screenScanProgress + 2}%`
                        }}
                      />
                      
                      {/* Subtle grid overlay */}
                      <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'linear-gradient(cyan 1px, transparent 1px), linear-gradient(90deg, cyan 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                      }} />
                      
                      {/* Corner brackets - more prominent */}
                      <div className="absolute top-3 left-3 w-16 h-16 border-t-[3px] border-l-[3px] border-cyan-400 animate-pulse shadow-[0_0_15px_rgba(6,182,212,0.6)]" />
                      <div className="absolute top-3 right-3 w-16 h-16 border-t-[3px] border-r-[3px] border-cyan-400 animate-pulse shadow-[0_0_15px_rgba(6,182,212,0.6)]" />
                      <div className="absolute bottom-3 left-3 w-16 h-16 border-b-[3px] border-l-[3px] border-cyan-400 animate-pulse shadow-[0_0_15px_rgba(6,182,212,0.6)]" />
                      <div className="absolute bottom-3 right-3 w-16 h-16 border-b-[3px] border-r-[3px] border-cyan-400 animate-pulse shadow-[0_0_15px_rgba(6,182,212,0.6)]" />
                      
                      {/* Scanning status overlay at top */}
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-background/95 via-background/80 to-transparent pt-3 pb-8">
                        <div className="flex flex-col items-center gap-2">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />
                            <p className="text-sm font-bold text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]">
                              OCR Scanning in Progress
                            </p>
                          </div>
                          <p className="text-xs text-cyan-300/90 font-medium">
                            {screenScanProgress < 25 && "Initializing document analysis..."}
                            {screenScanProgress >= 25 && screenScanProgress < 50 && "Capturing text regions..."}
                            {screenScanProgress >= 50 && screenScanProgress < 75 && "Extracting data fields..."}
                            {screenScanProgress >= 75 && "Finalizing results..."}
                          </p>
                        </div>
                      </div>
                      

                      
                      {/* Floating data particles */}
                      {[...Array(12)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(6,182,212,0.8)]"
                          style={{
                            left: `${10 + Math.random() * 80}%`,
                            top: `${10 + Math.random() * 80}%`,
                            animationDelay: `${Math.random() * 2}s`,
                            animationDuration: `${1 + Math.random()}s`,
                            opacity: 0.3 + Math.random() * 0.5
                          }}
                        />
                      ))}
                      
                      {/* Scanning highlight zones */}
                      <div 
                        className="absolute left-0 right-0 h-20 bg-gradient-to-b from-cyan-500/5 to-transparent"
                        style={{
                          top: `${Math.max(0, screenScanProgress - 10)}%`
                        }}
                      />
                    </div>
                  )}
                  

                </div>

                {/* Progress Section */}
                <div className="space-y-3">
                  {/* Progress Bar */}
                  <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all duration-300 ease-out",
                        isComplete && "bg-green-500",
                        isError && "bg-destructive",
                        isProcessing && "bg-primary animate-pulse"
                      )}
                      style={{ width: `${scanProgress.progress}%` }}
                    />
                  </div>

                  {/* Status Message */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isProcessing && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                      {isComplete && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                      {isError && (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}
                      <span className={cn(
                        "text-sm font-medium",
                        isComplete && "text-green-500",
                        isError && "text-destructive"
                      )}>
                        {scanProgress.message}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {scanProgress.progress}%
                    </span>
                  </div>

                  {/* Scan Details */}
                  {isProcessing && (
                    <div className="space-y-1 text-xs text-muted-foreground">
                      {scanProgress.stage === 'uploading' && (
                        <p>• Uploading image to server...</p>
                      )}
                      {scanProgress.stage === 'scanning' && (
                        <>
                          <p>• Analyzing document structure...</p>
                          <p>• Detecting text regions...</p>
                        </>
                      )}
                      {scanProgress.stage === 'extracting' && (
                        <>
                          <p>• Extracting merchant information...</p>
                          <p>• Parsing transaction details...</p>
                          <p>• Identifying amounts and dates...</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                  <span className="font-medium truncate flex-1">{selectedFile.name}</span>
                  <span className="ml-2">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              {!isProcessing && !isComplete && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isProcessing}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  {selectedFile && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleBrowseClick}
                        disabled={isProcessing}
                        className="w-full sm:w-auto"
                      >
                        Choose Different File
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={isProcessing || !selectedReceiptType}
                        className="w-full sm:w-auto"
                      >
                        <ScanLine className="h-4 w-4 mr-2" />
                        Scan Receipt
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>

        <style>{`
          @keyframes scan {
            0% {
              transform: translateY(-100%);
            }
            100% {
              transform: translateY(100%);
            }
          }

          .scan-line {
            position: absolute;
            width: 100%;
            height: 4px;
            background: linear-gradient(
              to bottom,
              transparent,
              hsl(var(--primary)) 50%,
              transparent
            );
            box-shadow: 0 0 20px hsl(var(--primary));
            animation: scan 2s ease-in-out infinite;
          }

          .grid-overlay {
            background-image: 
              linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px);
            background-size: 20px 20px;
          }
        `}</style>
      </Dialog>

      <ReviewReceiptDialog
        open={reviewDialogOpen}
        onOpenChange={handleReviewDialogClose}
        receipt={scannedReceipt}
        onConfirm={handleReviewConfirm}
        onDiscard={handleReviewDiscard}
      />
    </>
  );
}
